<template>
	<section class="py-20 overflow-hidden will-change" data-scroll-section>
		<div class="px-content mb-12">
			<h2 class="text-4xl xl:text-heading mb-8">
				{{ getSectionTitle() }}
			</h2>
		</div>

		<template v-if="!isMobile">
			<div class="px-content grid grid-cols-1 md:grid-cols-3 gap-3 xl:gap-6">
				<a
					v-for="(card, index) in cards"
					v-show="card.content?.rendered || card.title?.rendered"
					:key="index"
					:href="card.link"
					class="flex flex-col group"
				>
					<div
						class="overflow-hidden relative mb-6 after:block after:bg-black after:opacity-20 after:absolute after:w-full after:h-full after:z-[8] after:top-0 after:left-0 group-hover:after:opacity-50 after:transition-opacity"
					>
						<img
							class="object-cover w-full h-full transition-transform group-hover:scale-105 duration-500"
							:src="getImageSrc(card)"
							:alt="getImageAlt(card)"
							v-if="getImageSrc(card)"
						/>
					</div>
					<div
						class="card-toponyms__txt pb-[18px] mt-0 flex flex-col justify-between"
					>
						<div>
							<span
								class="block text-sm leading-[1.42] mb-3"
								v-show="card.date"
								v-html="formatDate(card.date)"
							></span>
							<h4
								class="text-2xl tracking-[1px] mb-5"
								v-html="card.title?.rendered"
							></h4>
						</div>
						<div>
							<p class="text-md mb-3">
								{{ getExcerpt(card) }}
							</p>
							<span
								class="text-xs tracking-little text-navy-blue-02 font-bold uppercase mt-auto"
							>
								{{ getSeeMoreText() }}
							</span>
						</div>
					</div>
				</a>
			</div>
		</template>

		<!-- Mobile slider -->
		<template v-else>
			<div class="custom-slider">
				<div class="swiper-container" ref="exploreCards">
					<div class="swiper-wrapper pl-content">
						<div
							v-for="(card, index) in cards"
							:key="index"
							class="swiper-slide"
						>
							<a
								class="block w-full h-full relative overflow-hidden cursor-pointer after:block after:bg-black after:opacity-20 after:absolute after:w-full after:h-full after:z-[8] after:top-0 after:left-0 hover:after:opacity-50 after:transition-opacity"
								:href="card.link"
							>
								<img
									class="object-cover w-full"
									:src="getImageSrc(card)"
									:alt="getImageAlt(card)"
									v-if="getImageSrc(card)"
								/>
								<h4
									class="text-lg xl:text-2xl font-normal text-white text-center px-4 w-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
									v-html="card.title?.rendered"
								></h4>
							</a>
						</div>
					</div>
				</div>
			</div>
		</template>
	</section>
</template>

<script>
import { format, parse } from "date-fns";
import Swiper from "swiper/bundle";
import "swiper/swiper-bundle.css";

export default {
	name: "ReadNextDefaultPost",
	data() {
		return {
			isToponym: false,
			cards: [],
			currentId: null,
			exploreCards: null,
			exploreCardsOptions: {
				slidesPerView: 1,
				spaceBetween: 16,
				breakpoints: {
					320: { slidesPerView: 1, spaceBetween: 16 },
					480: { slidesPerView: 2, spaceBetween: 24 },
				},
			},
		};
	},

	mounted() {
		this.isToponym = document.body.classList.contains("single-toponym");

		this.currentId = this.postData?.ID || window.currentPostId || null;

		this.getPosts();
		this.initSwiper();
	},

	methods: {
		initSwiper() {
			this.$nextTick(() => {
				if (this.$refs.exploreCards) {
					this.exploreCards = new Swiper(
						this.$refs.exploreCards,
						this.exploreCardsOptions
					);
				}
			});
		},

		currentLang() {
			const path = window.location.pathname.toLowerCase();
			if (path.startsWith("/ru/")) return "ru";
			if (path.startsWith("/me/")) return "me";
			if (path.startsWith("/de/")) return "de";
			return "en";
		},

		getSectionTitle() {
			const lang = this.currentLang();

			if (this.isToponym) {
				switch (lang) {
					case "ru":
						return "Другие топонимы";
					case "me":
					case "sr":
						return "Ostali toponimi";
					case "de":
						return "Weitere Toponyme";
					default:
						return "Other Toponyms";
				}
			} else {
				switch (lang) {
					case "ru":
						return "Читайте далее";
					case "me":
					case "sr":
						return "Pročitajte sljedeće";
					case "de":
						return "Weiterlesen";
					default:
						return "Read Next";
				}
			}
		},

		getSeeMoreText() {
			const lang = this.currentLang();

			switch (lang) {
				case "ru":
					return "Подробнее";
				case "me":
				case "sr":
					return "Pogledajte više";
				case "de":
					return "Mehr sehen";
				default:
					return "SEE MORE";
			}
		},

		getPosts() {
			const base = this.endpoints.rest_base_endpoint + "wp/v2/";
			let endpoint = this.isToponym ? "toponym" : "posts";

			let url = `${base}${endpoint}?per_page=6`;

			if (this.currentId) {
				url += `&exclude=${this.currentId}`;
			}

			this.$http
				.get(url)
				.then((res) => {
					let items = res.data || [];

					items = this.shuffle(items);

					this.cards = items.slice(0, 3);
				})
				.catch((err) => {
					console.error("Failed to load read next items:", err);
				});
		},

		shuffle(array) {
			const newArray = [...array];
			for (let i = newArray.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
			}
			return newArray;
		},

		getImageSrc(card) {
			if (!card) return null;

			if (card.data?.fields?.image?.sizes?.customslide) {
				return card.data.fields.image.sizes.customslide;
			}
			if (card.data?.fields?.image?.url) {
				return card.data.fields.image.url;
			}

			return (
				card.featured_image?.sizes?.customslide ||
				card.featured_image?.url ||
				card._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
				null
			);
		},

		getImageAlt(card) {
			if (card.data?.fields?.image?.alt) return card.data.fields.image.alt;
			return card.title?.rendered || "";
		},

		formatDate(date) {
			if (!date) return "";
			let parsed = parse(date, "yyyy-MM-dd'T'HH:mm:ss", new Date());
			return format(parsed, "dd MMM yyyy");
		},

		getFirstLines(content) {
			if (!content) return "";

			const temp = document.createElement("div");
			temp.innerHTML = content;

			// Uklanjamo sve neželjene elemente sa početka
			// 1. Tabele (najveći problem kod toponima)
			temp.querySelectorAll("table").forEach((el) => el.remove());

			// 2. Naslove
			temp
				.querySelectorAll("h1, h2, h3, h4, h5, h6")
				.forEach((el) => el.remove());

			// 3. Prvih nekoliko paragrafа koji su prekratki ili meta (npr. "Encyclopedic Overview")
			const paragraphs = Array.from(temp.querySelectorAll("p"));
			paragraphs.forEach((p, index) => {
				const text = p.textContent.trim();
				if (
					index < 3 &&
					(text.length < 60 ||
						text.includes("Encyclopedic Overview") ||
						text.includes("Toponym Type") ||
						text.includes("Country"))
				) {
					p.remove();
				}
			});

			let text = temp.textContent || temp.innerText || "";

			// Čišćenje
			text = text.replace(/\s+/g, " ").trim();

			// Uzimamo razumnu dužinu
			const maxLength = 165;
			if (text.length > maxLength) {
				text = text.substring(0, maxLength).trim() + " ...";
			}

			return text;
		},
		
		getExcerpt(card) {
			if (card.acf?.excerpt || card.data?.fields?.excerpt) {
				const excerpt = card.acf?.excerpt || card.data?.fields?.excerpt;
				return excerpt.length > 165
					? excerpt.substring(0, 165).trim() + " ..."
					: excerpt;
			}
			return this.getFirstLines(card.content?.rendered || "");
		},
	},

	beforeDestroy() {
		if (this.exploreCards) {
			this.exploreCards.destroy();
		}
	},
};
</script>
